import { ArrowLeft, ArrowRight } from "@carbon/icons-react";
import {
  Button,
  Card,
  CardContent,
  List,
  ListItemText,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import { IDeliveryItem } from "../../../../models/deliveryItem";
import TransferDialog from "../transferDialog/TransferDialog";
import "./TransferableList.scss";

export interface TransferListItem {
  item: IDeliveryItem;
  list: "original" | "new";
}

interface TransferableListProps {
  originalItems: IDeliveryItem[];
  newItems: IDeliveryItem[];
  disableTransferButtons: boolean;
  setOriginalItems: Dispatch<SetStateAction<IDeliveryItem[]>>;
  setNewItems: Dispatch<SetStateAction<IDeliveryItem[]>>;
}

const TransferableList = (props: TransferableListProps) => {
  const { t } = useTranslation();
  const {
    originalItems,
    newItems,
    setOriginalItems,
    setNewItems,
    disableTransferButtons,
  } = props;

  const [selectedItem, setSelectedItem] = useState<TransferListItem | null>(
    null,
  );
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const handleTransfer = (quantityToTransfer: number) => {
    if (!selectedItem || isNaN(quantityToTransfer)) return;

    const originList =
      selectedItem.list === "original" ? originalItems : newItems;
    const setOriginList =
      selectedItem.list === "original" ? setOriginalItems : setNewItems;

    const destinationList =
      selectedItem.list === "original" ? newItems : originalItems;
    const setDestinationList =
      selectedItem.list === "original" ? setNewItems : setOriginalItems;

    const updatedOriginItems = originList
      .map((i) =>
        i.id === selectedItem.item.id
          ? { ...i, quantity: i.quantity - quantityToTransfer }
          : i,
      )
      .filter((i) => i.quantity > 0); // Remove items with quantity 0;

    const existingNewItem = destinationList.find(
      (i) => i.id === selectedItem.item.id,
    );

    const updatedDestinationItems = existingNewItem
      ? destinationList.map((i) =>
          i.id === selectedItem.item.id
            ? {
                ...i,
                quantity: i.quantity + quantityToTransfer,
              }
            : i,
        )
      : [
          ...destinationList,
          { ...selectedItem.item, quantity: quantityToTransfer },
        ];

    setOriginList(updatedOriginItems.sort((a, b) => a.position - b.position));
    setDestinationList(
      updatedDestinationItems.sort((a, b) => a.position - b.position),
    );

    setSelectedItem(null);
  };

  const handleSelectItem = (item: IDeliveryItem, list: "original" | "new") => {
    setSelectedItem({ item, list });
  };

  const renderList = (items: IDeliveryItem[], type: "original" | "new") => {
    return (
      <div className="transferList-container">
        <Typography variant="h6">{t(type + "List")}</Typography>
        <List className="transferList-container-list">
          {items.map((item) => (
            <Card variant="outlined" key={item.id + type}>
              <CardContent
                className={`transferList-container-list-item${
                  selectedItem?.item.id === item.id &&
                  selectedItem?.list === type
                    ? "-selected"
                    : ""
                }`}
                onClick={() => handleSelectItem(item, type)}
              >
                <ListItemText
                  primary={`${item.position} - ${item.material} - ${item.stockType} `}
                  secondary={`${t("quantity")}: ${item.quantity} ${item.unitOfMeasurement}`}
                />
              </CardContent>
            </Card>
          ))}
        </List>
      </div>
    );
  };

  const renderTransferButton = (type: "original" | "new") => {
    return (
      <Button
        variant="contained"
        color="primary"
        disabled={
          disableTransferButtons || !selectedItem || selectedItem.list !== type
        }
        onClick={() => setShowDialog(true)}
      >
        {type === "original" ? <ArrowRight /> : <ArrowLeft />}
      </Button>
    );
  };

  return (
    <div>
      <Typography align="center" marginBottom="24px" variant="h6">
        {t("splitDeliveryDescription")}
      </Typography>
      <div className="transferList">
        {renderList(originalItems, "original")}
        <div className="transferList-button-container">
          {renderTransferButton("original")}
          {renderTransferButton("new")}
        </div>
        {renderList(newItems, "new")}
        {showDialog && selectedItem && (
          <TransferDialog
            showDialog={showDialog}
            closeDialog={() => setShowDialog(false)}
            selectedItem={selectedItem}
            handleTransfer={handleTransfer}
          />
        )}
      </div>
    </div>
  );
};

export default TransferableList;
